import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalFonts,
  signalGradients,
  signalText,
} from '@/theme/foundations/tokens';

interface StepRailProps {
  /** Step labels in order, e.g. ["STEP 1", "STEP 2"]. */
  steps: string[];
  /** Zero-based index of the step being shown. */
  active: number;
  gap?: string;
  sx?: SxProps<Theme>;
}

/**
 * The progress rail above a multi-step panel.
 *
 * Only the rule immediately *before* the active step is filled with the accent
 * color; everything else stays a plain divider. That way the fill reads as
 * distance already travelled rather than as decoration, and step one — which has
 * nothing behind it — correctly shows no fill at all.
 */
export const StepRail: React.FC<StepRailProps> = ({
  steps,
  active,
  gap = '10px',
  sx,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap,
      fontFamily: signalFonts.mono,
      fontWeight: 600,
      fontSize: '10px',
      letterSpacing: '0.14em',
      color: signalText.muted,
      ...sx,
    }}
  >
    {steps.map((label, i) => (
      <React.Fragment key={label}>
        {i > 0 && (
          <Box
            component="span"
            sx={{
              flex: 1,
              height: '1px',
              background:
                i === active ? signalGradients.stepRule : signalBorders.medium,
            }}
          />
        )}
        <Box
          component="span"
          sx={{ color: i === active ? signalColors.accent : undefined }}
        >
          {label}
        </Box>
      </React.Fragment>
    ))}
  </Box>
);

export default StepRail;
